import React, { useEffect, useState, useCallback } from "react";
import { useAutoRefresh, AUTO_REFRESH_MS } from "../../hooks/useAutoRefresh";
import DateInput from "../../components/Common/DateInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Container, Row, Col, Spinner, Alert } from "reactstrap";
import { jsPDF } from "jspdf";
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import { fetchPosAnalytics } from "../../api/requests/pos-analytics-requests";
import { useProfileUser } from "../../feautures/settings/hooks/useProfileUser";
import PosScopeTable from "./PosReport/PosScopeTable";
import {
    PosAdvancedFilters,
    filterPosRows,
    fmtMoney2,
    sortPosRows,
    usePosFilters,
    usePosSort,
} from "./PosReport/PosReportShared";

import RefreshIndicator from "../../components/Common/RefreshIndicator";
const NAVY = "#0A316E";
const ORANGE = "#E8833A";
const LIGHT_BG = "#f6f7fb";

// Donut renk paleti (görsel ile uyumlu)
const SLICE_COLORS = ["#0A316E", "#E8833A", "#5FB98E", "#5B8DEF", "#E7B84B", "#9B6FD4", "#9aa3b2"];

/**
 * "POS Entegrasyon Detayları" tablosunun gelişmiş filtreleri. Tablo tek seferde
 * geldiği için filtreleme yereldir — sunucuya yeni istek atılmaz.
 */
const FILTER_FIELDS = [
    { key: "search", type: "text", label: "POS Sağlayıcı", placeholder: "Sağlayıcı adında ara" },
    { key: "active", type: "select", label: "Durum",
      options: [{ value: "true", label: "Aktif" }, { value: "false", label: "Pasif" }] },
    { key: "transactionCount", type: "range", label: "İşlem Sayısı",
      minKey: "minTransactionCount", maxKey: "maxTransactionCount" },
    { key: "grossAmount", type: "range", label: "İşlem Tutarı (₺)",
      minKey: "minGrossAmount", maxKey: "maxGrossAmount" },
    { key: "netAmount", type: "range", label: "Net Tutar (₺)",
      minKey: "minNetAmount", maxKey: "maxNetAmount" },
];

const PERIODS = [
    { key: "week", label: "Bu Hafta" },
    { key: "month", label: "Bu Ay" },
    { key: "quarter", label: "Bu Çeyrek" },
    { key: "year", label: "Yıllık" },
];

const trNum = (n) => new Intl.NumberFormat("tr-TR").format(Math.round(Number(n ?? 0)));

const fmtTL = (n) => "₺" + trNum(n);

const fmtCompactTL = (n) => {
    const v = Number(n ?? 0);
    if (Math.abs(v) >= 1_000_000) return "₺" + (v / 1_000_000).toFixed(2) + "M";
    if (Math.abs(v) >= 1_000) return "₺" + (v / 1_000).toFixed(0) + "K";
    return "₺" + trNum(v);
};

// Değişim metnine göre renk/ok belirle ("+12.4%", "-3", "Yeni", "+0.4 puan")
const changeMeta = (change) => {
    if (!change) return { color: "#9aa3b2", icon: "bx-minus" };
    if (change.startsWith("-")) return { color: "#dc3545", icon: "bx-trending-down" };
    if (change.startsWith("+") || change === "Yeni") return { color: "#2BA66B", icon: "bx-trending-up" };
    return { color: "#9aa3b2", icon: "bx-minus" };
};

const SectionTitle = ({ children }) => (
    <div className="d-flex align-items-center gap-3 mb-3 mt-2">
        <span
            className="fw-bold text-uppercase"
            style={{ color: "#8a93a6", fontSize: ".72rem", letterSpacing: ".06em" }}
        >
            {children}
        </span>
        <div style={{ flex: 1, height: 1, background: "#e7eaf0" }} />
    </div>
);

const KpiCard = ({ icon, label, value, change, sub, accent }) => {
    const cm = changeMeta(change);
    return (
        <div
            className="bg-white h-100 p-3 d-flex flex-column"
            style={{
                borderRadius: 16,
                border: "1px solid #eef0f4",
                borderTop: accent ? `3px solid ${ORANGE}` : "1px solid #eef0f4",
                boxShadow: "0 2px 8px rgba(10,49,110,0.04)",
            }}
        >
            <div
                className="d-flex align-items-center justify-content-center mb-3"
                style={{ width: 40, height: 40, borderRadius: 12, background: "#eef2f9" }}
            >
                <i className={`bx ${icon}`} style={{ color: NAVY, fontSize: "1.25rem" }}></i>
            </div>
            <div className="text-muted mb-1" style={{ fontSize: ".8rem" }}>{label}</div>
            <div className="fw-bold text-dark mb-1"
                 style={{ fontSize: "1.15rem", lineHeight: 1.15, wordBreak: "break-word" }}>
                {value}
            </div>
            {change && (
                <div className="d-flex align-items-center gap-1" style={{ fontSize: ".75rem", color: cm.color }}>
                    <i className={`bx ${cm.icon}`}></i>
                    <span>{change}</span>
                </div>
            )}
            {/* Kaldırılan kartların bilgisi (başarı oranı, iade, sarkan valör) burada yaşar */}
            {sub && <div className="text-muted" style={{ fontSize: ".72rem" }}>{sub}</div>}
        </div>
    );
};

/* ----------- PDF için Roboto (Türkçe destekli) fontu — runtime'da /fonts'tan yüklenir ----------- */
let _robotoB64 = null;
async function loadRobotoB64() {
    if (_robotoB64) return _robotoB64;
    const url = (process.env.PUBLIC_URL || "") + "/fonts/Roboto-Regular.ttf";
    const buf = await (await fetch(url)).arrayBuffer();
    const bytes = new Uint8Array(buf);
    let bin = "";
    const CH = 0x8000;
    for (let i = 0; i < bytes.length; i += CH) {
        bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CH));
    }
    _robotoB64 = btoa(bin);
    return _robotoB64;
}

export default function POSAnalysisDashboard() {
    const navigate = useNavigate();
    // Dönem query string'de tutulur; hiyerarşide alt ekranlara geçip geri dönerken korunur.
    const [searchParams, setSearchParams] = useSearchParams();
    const period = searchParams.get("period") || "month";
    // Hazır dönem seçilince serbest tarih aralığı temizlenir (ikisi aynı anda geçerli olamaz).
    const setPeriod = useCallback((next) => {
        const p = new URLSearchParams(searchParams);
        p.set("period", next);
        p.delete("start");
        p.delete("end");
        setSearchParams(p, { replace: true });
    }, [searchParams, setSearchParams]);

    // Serbest tarih aralığı; boşsa backend dönem varsayılanını uygular.
    const customStart = searchParams.get("start") || "";
    const customEnd = searchParams.get("end") || "";
    const applyCustomRange = useCallback((nextStart, nextEnd) => {
        const p = new URLSearchParams(searchParams);
        if (nextStart) p.set("start", nextStart); else p.delete("start");
        if (nextEnd) p.set("end", nextEnd); else p.delete("end");
        setSearchParams(p, { replace: true });
    }, [searchParams, setSearchParams]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    // PDF: native jsPDF ile (metin + tablo) frontta üretilir — screenshot yok.
    const [pdfBusy, setPdfBusy] = useState(false);

    // "POS Entegrasyon Detayları" tablosunun kolon sıralaması. Tüm sağlayıcılar
    // tek seferde geldiği için (sayfalama yok) ağır analiz sorgusunu tekrarlamak
    // yerine yerel sıralanır; CSV/PDF de ekrandaki sırayı izler.
    const { sort, direction, toggle } = usePosSort("grossAmount", "desc");
    const { draft, applied, set, commit, reset, activeCount } = usePosFilters();
    // Kartlar tablo ile aynı kümeden beslenir (başarılı + kısmi iade edilmiş işlemler);
    // KPI sorgusu tüm statüleri saydığı için sayılar aksi halde birbirini tutmuyordu.
    const totals = data?.detailsTotals || {};
    const matrah = Number(totals.grossAmount ?? 0) - Number(totals.refundedAmount ?? 0);
    const effectiveRate = matrah > 0 ? (Number(totals.commissionAmount ?? 0) / matrah) * 100 : null;
    const pendingValor = Number(totals.netAmount ?? 0) - Number(totals.valorAmount ?? 0);
    const providerShare = totals.commissionEarning != null
        ? Number(totals.commissionAmount ?? 0) - Number(totals.commissionEarning)
        : null;
    const earningShareOfCommission = totals.commissionEarning != null && Number(totals.commissionAmount) > 0
        ? (Number(totals.commissionEarning) / Number(totals.commissionAmount)) * 100
        : null;

    const details = sortPosRows(
        filterPosRows(data?.details || [], applied),
        sort, direction
    );

    // Rol/firma bilgisi (mevcut auth/me pattern'i ile — tek çağrı, roller + firma adı).
    const { me, roles, loadingUser } = useProfileUser();
    const isSuperAdmin = roles.includes("ROLE_SUPER_ADMIN");
    // Firma kapsamını yalnızca COMPANY_ADMIN kesinleştikten sonra göster:
    // yüklenirken varsayılan "Tüm entegrasyonlar" kalır, SUPER_ADMIN görünümü değişmez.
    const showCompanyScope = !loadingUser && !isSuperAdmin && !!me?.companyName;

    /**
     * @param {{silent?: boolean}} [opts] silent=true otomatik yenilemede kullanılır:
     * grafikler spinner'a dönmez, hata basılmaz; seçili dönem/tarih aralığı korunur.
     */
    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const res = await fetchPosAnalytics(period, customStart || undefined, customEnd || undefined);
            if (res?.statusCode === 200 && res.data) {
                setData(res.data);
            } else if (!silent && res?.statusCode === 403) {
                setError("Bu sayfayı görüntüleme yetkiniz yok.");
            } else if (!silent) {
                setError(res?.message || "POS analizi alınamadı.");
            }
        } catch (e) {
            if (!silent) setError("POS analizi yüklenirken bir hata oluştu.");
        } finally {
            if (!silent) setLoading(false);
        }
    }, [period, customStart, customEnd]);

    // load seçili dönemi kapsadığı için sessiz çağrı filtreyi bozmadan tazeler.
    const autoRefresh = useAutoRefresh(() => load({ silent: true }), { intervalMs: AUTO_REFRESH_MS });

    useEffect(() => {
        load();
    }, [load]);

    const exportCsv = () => {
        if (!data) return;
        const rows = [
            [
                "POS Sağlayıcı",
                // Ekranda olduğu gibi: süper admin kurulum sayısını, firma kendi durumunu görür
                isSuperAdmin ? "Kurulu Firma Sayısı" : "Durum",
                "İşlem Sayısı", "Ortalama Tutar", "İşlem Tutarı",
                "Komisyon Tutarı", "İptal/İade Toplamı", "Net Tutar", "Valör Toplamı",
            ],
            ...details.map((d) => [
                d.providerLabel,
                isSuperAdmin ? (d.configuredCompanyCount ?? 0) : (d.active ? "Aktif" : "Pasif"),
                d.transactionCount ?? 0,
                Math.round(d.avgAmount ?? 0),
                Math.round(d.grossAmount ?? 0),
                Math.round(d.commissionAmount ?? 0),
                Math.round(d.refundedAmount ?? 0),
                Math.round(d.netAmount ?? 0),
                Math.round(d.valorAmount ?? 0),
            ]),
        ];
        const csv = rows.map((r) => r.join(";")).join("\n");
        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `pos-rapor-${data.periodKey || period}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    // PDF'i native jsPDF ile (metin + tablolar) üret — screenshot yok, kayma/artefakt yok.
    const exportPdf = async () => {
        if (!data || pdfBusy) return;
        setPdfBusy(true);
        try {
            const pdf = new jsPDF("p", "mm", "a4");
            const b64 = await loadRobotoB64();
            pdf.addFileToVFS("Roboto-Regular.ttf", b64);
            pdf.addFont("Roboto-Regular.ttf", "Roboto", "normal");
            pdf.setFont("Roboto", "normal");

            const PW = pdf.internal.pageSize.getWidth();
            const PH = pdf.internal.pageSize.getHeight();
            const M = 14;
            const CW = PW - M * 2;

            const cNavy = [10, 49, 110];
            const cGray = [138, 147, 166];
            const cDark = [33, 37, 41];
            const cBorder = [230, 234, 240];
            const cHead = [238, 242, 249];
            const cGreen = [30, 143, 91];
            const cAmber = [220, 162, 26];
            const cRed = [220, 53, 69];

            let y = M;
            const ensure = (need) => { if (y + need > PH - M) { pdf.addPage(); y = M; } };

            // ---- Başlık ----
            const scope = showCompanyScope ? me.companyName : "Tüm entegrasyonlar";
            pdf.setFontSize(18); pdf.setTextColor(...cNavy);
            pdf.text("POS Rapor", M, y + 5);
            pdf.setFontSize(9); pdf.setTextColor(...cGray);
            pdf.text(new Date().toLocaleString("tr-TR"), PW - M, y + 5, { align: "right" });
            y += 11;
            pdf.setFontSize(10); pdf.setTextColor(...cGray);
            const rangeTxt = data.periodLabel || period;
            pdf.text(`Dönem: ${periodName ? periodName + " (" + rangeTxt + ")" : rangeTxt} · ${scope}`, M, y);
            y += 4;
            pdf.setDrawColor(...cBorder); pdf.setLineWidth(0.3);
            pdf.line(M, y, PW - M, y);
            y += 8;

            const section = (title) => {
                ensure(12);
                pdf.setFontSize(8); pdf.setTextColor(...cGray);
                pdf.text(title.toUpperCase(), M, y);
                y += 2;
                pdf.setDrawColor(...cBorder); pdf.setLineWidth(0.3);
                pdf.line(M, y, PW - M, y);
                y += 6;
            };

            // ---- KPI kartları (3 x 2) ----
            const kp = data.kpis || {};
            const cards = [
                { label: "Toplam Tahsilat", value: fmtMoney2(totals.grossAmount), change: kp.totalAmountChange },
                { label: "İşlem Sayısı", value: trNum(totals.transactionCount), change: kp.totalTransactionsChange },
                { label: "Taksit Dağılımı", value: `%${(kp.installmentRate ?? 0).toFixed(0)} taksitli / %${(kp.singleRate ?? 0).toFixed(0)} tek çekim`, change: null },
                { label: isSuperAdmin ? "Toplam Komisyon" : "Komisyon Kesintisi", value: fmtMoney2(totals.commissionAmount), change: null },
                { label: "Net Tutar", value: fmtMoney2(totals.netAmount), change: null },
                { label: "Valörü Bu Dönemde", value: fmtMoney2(totals.valorAmount), change: null },
                isSuperAdmin
                    ? { label: "Komisyon Kazancı", value: totals.commissionEarning != null ? fmtMoney2(totals.commissionEarning) : "—", change: null }
                    : { label: "Ort. Komisyon Oranı", value: effectiveRate != null ? `%${effectiveRate.toFixed(2)}` : "—", change: null },
            ];
            section("Genel İstatistikler");
            const cols = 3, gap = 4, cardW = (CW - gap * (cols - 1)) / cols, cardH = 21;
            ensure(cardH * 2 + gap);
            const gridTop = y;
            cards.forEach((c, i) => {
                const cx = M + (i % cols) * (cardW + gap);
                const cy = gridTop + Math.floor(i / cols) * (cardH + gap);
                pdf.setDrawColor(...cBorder); pdf.setFillColor(255, 255, 255);
                pdf.roundedRect(cx, cy, cardW, cardH, 2, 2, "S");
                pdf.setFontSize(8); pdf.setTextColor(...cGray);
                pdf.text(c.label, cx + 4, cy + 6);
                pdf.setFontSize(14); pdf.setTextColor(...cDark);
                pdf.text(String(c.value), cx + 4, cy + 14);
                if (c.change) {
                    const neg = String(c.change).startsWith("-");
                    pdf.setFontSize(8); pdf.setTextColor(...(neg ? cRed : cGreen));
                    pdf.text(String(c.change), cx + 4, cy + 19);
                }
            });
            y = gridTop + cardH * 2 + gap + 8;

            // ---- Tablo çizici ----
            const drawTable = (headers, rows, aligns, widths) => {
                const rowH = 7;
                const header = () => {
                    pdf.setFillColor(...cHead);
                    pdf.rect(M, y, CW, rowH, "F");
                    pdf.setFontSize(8); pdf.setTextColor(...cNavy);
                    let cx = M;
                    headers.forEach((h, i) => {
                        const a = aligns[i] || "left";
                        const tx = a === "right" ? cx + widths[i] - 2 : cx + 2;
                        pdf.text(String(h), tx, y + 4.7, { align: a });
                        cx += widths[i];
                    });
                    y += rowH;
                };
                ensure(rowH * 2);
                header();
                rows.forEach((r) => {
                    if (y + rowH > PH - M) { pdf.addPage(); y = M; header(); }
                    let cx = M;
                    r.forEach((cell, i) => {
                        const isObj = cell && typeof cell === "object";
                        pdf.setFontSize(8);
                        pdf.setTextColor(...((isObj && cell.color) || cDark));
                        const a = aligns[i] || "left";
                        const tx = a === "right" ? cx + widths[i] - 2 : cx + 2;
                        pdf.text(String(isObj ? cell.text : cell), tx, y + 4.7, { align: a });
                        cx += widths[i];
                    });
                    pdf.setDrawColor(...cBorder); pdf.setLineWidth(0.2);
                    pdf.line(M, y + rowH, M + CW, y + rowH);
                    y += rowH;
                });
                y += 7;
            };

            // ---- POS Dağılımı ----
            const dist = data.distribution || [];
            if (dist.length) {
                section("POS Dağılımı");
                drawTable(
                    ["Sağlayıcı", "Tutar", "Oran"],
                    dist.map((d) => [d.providerLabel, fmtTL(d.amount), `%${(d.percentage ?? 0).toFixed(0)}`]),
                    ["left", "right", "right"],
                    [CW * 0.5, CW * 0.3, CW * 0.2]
                );
            }

            // ---- Trend ----
            const tr = data.trend || [];
            if (tr.length) {
                section("Tahsilat & İşlem Trendi");
                drawTable(
                    ["Dönem", "Tahsilat", "İşlem Adedi"],
                    tr.map((t) => [t.label, fmtTL(t.amount), trNum(t.transactionCount)]),
                    ["left", "right", "right"],
                    [CW * 0.4, CW * 0.32, CW * 0.28]
                );
            }

            // ---- POS Entegrasyon Detayları ----
            section("POS Entegrasyon Detayları");
            const det = details;
            if (!det.length) {
                pdf.setFontSize(9); pdf.setTextColor(...cGray);
                pdf.text("Bu dönemde POS işlemi bulunmuyor.", M, y + 3);
                y += 8;
            } else {
                drawTable(
                    ["Sağlayıcı", "İşlem", "Ort. Tutar", "İşlem Tutarı", "Komisyon", "İptal/İade", "Net", "Valör"],
                    det.map((d) => [
                        d.providerLabel,
                        trNum(d.transactionCount),
                        fmtTL(d.avgAmount),
                        fmtTL(d.grossAmount),
                        fmtTL(d.commissionAmount),
                        fmtTL(d.refundedAmount),
                        fmtTL(d.netAmount),
                        fmtTL(d.valorAmount),
                    ]),
                    ["left", "right", "right", "right", "right", "right", "right", "right"],
                    [CW * 0.16, CW * 0.08, CW * 0.13, CW * 0.14, CW * 0.13, CW * 0.12, CW * 0.12, CW * 0.12]
                );
            }

            // ---- Alt bilgi (sayfa no) ----
            const pageCount = pdf.internal.getNumberOfPages();
            for (let p = 1; p <= pageCount; p++) {
                pdf.setPage(p);
                pdf.setFontSize(8); pdf.setTextColor(...cGray);
                pdf.text("Tahsilat360 · POS Rapor", M, PH - 6);
                pdf.text(`${p} / ${pageCount}`, PW - M, PH - 6, { align: "right" });
            }

            pdf.save(`pos-rapor-${data.periodKey || period}.pdf`);
        } catch (e) {
            setError("PDF oluşturulurken bir hata oluştu.");
        } finally {
            setPdfBusy(false);
        }
    };

    const kpis = data?.kpis;
    const trend = data?.trend || [];
    const distribution = data?.distribution || [];
    const totalDistAmount = distribution.reduce((s, d) => s + Number(d.amount ?? 0), 0);

    // Seçili dönemin okunur adı + gerçek tarih aralığı (backend periodLabel = aralık).
    const periodName = PERIODS.find((p) => p.key === period)?.label || "";
    // Trend kartı: rozet olarak dönemin gerçek tarih aralığı (backend periodLabel).
    const trendRange = data?.periodLabel || "";
    // Trend kırılımı seçili döneme göre değişir (backend bucket'lamasıyla eşleşir).
    const trendGranularity =
        period === "week" ? "Günlük kırılım"
            : period === "month" ? "Haftalık kırılım"
            : "Aylık kırılım"; // quarter, year

    return (
        <div className="page-content" style={{ background: LIGHT_BG, minHeight: "100vh" }}>
            <style>{`
              /* KPI satırı xl'de yediye bölünür. Bootstrap'te 12/7 kolon ve
                 row-cols-xl-7 yardımcısı yok; kart sayısı değişirse burası da güncellenmeli. */
              @media (min-width: 1200px) {
                .kpi-row > [class*="col-"] { flex: 0 0 14.2857%; max-width: 14.2857%; }
              }
            `}</style>
            <Container fluid>
                {/* Başlık + dönem seçici */}
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            <h3 className="mb-0 fw-bold" style={{ color: NAVY }}>POS Rapor</h3>
                            <RefreshIndicator {...autoRefresh} onClick={autoRefresh.refreshNow} />
                            {showCompanyScope && (
                                <span
                                    className="d-inline-flex align-items-center"
                                    style={{ background: "#eef2f9", color: NAVY, fontWeight: 600, fontSize: ".78rem", padding: ".3rem .7rem", borderRadius: 999 }}
                                >
                                    <i className="bx bx-buildings me-1" style={{ verticalAlign: "middle" }}></i>
                                    {me.companyName}
                                </span>
                            )}
                            {data?.periodLabel && (
                                <span
                                    className="d-inline-flex align-items-center"
                                    style={{ background: "#fff3e9", color: ORANGE, fontWeight: 600, fontSize: ".78rem", padding: ".3rem .7rem", borderRadius: 999, border: "1px solid #f6d9c2" }}
                                    title="Seçili dönemin tarih aralığı"
                                >
                                    <i className="bx bx-calendar me-1" style={{ verticalAlign: "middle" }}></i>
                                    {data.periodLabel}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {/* Serbest tarih aralığı — hazır dönemlerin solunda, diğer ekranlardaki desenle aynı */}
                        <div className="d-flex align-items-center gap-1 bg-white px-2 py-1"
                             style={{ borderRadius: 999, border: "1px solid #e7eaf0" }}>
                            <DateInput
                                className="form-control-sm border-0"
                                style={{ width: 140, boxShadow: "none" }}
                                value={customStart}
                                maxDate={customEnd || undefined}
                                onChange={(iso) => applyCustomRange(iso, customEnd)}
                            />
                            <span className="text-muted">–</span>
                            <DateInput
                                className="form-control-sm border-0"
                                style={{ width: 140, boxShadow: "none" }}
                                value={customEnd}
                                minDate={customStart || undefined}
                                onChange={(iso) => applyCustomRange(customStart, iso)}
                            />
                        </div>
                        <div className="d-flex bg-white p-1" style={{ borderRadius: 999, border: "1px solid #e7eaf0" }}>
                            {PERIODS.map((p) => (
                                <button
                                    key={p.key}
                                    onClick={() => setPeriod(p.key)}
                                    className="btn btn-sm px-3"
                                    style={{
                                        borderRadius: 999,
                                        fontSize: ".8rem",
                                        fontWeight: 600,
                                        border: "none",
                                        background: period === p.key ? NAVY : "transparent",
                                        color: period === p.key ? "#fff" : "#6c757d",
                                    }}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={exportCsv}
                            disabled={!data}
                            className="btn btn-sm d-flex align-items-center gap-1 px-3"
                            style={{ borderRadius: 999, background: "#fff", color: NAVY, fontWeight: 600, border: "1px solid #e7eaf0" }}
                        >
                            <i className="bx bx-download"></i> CSV
                        </button>
                        <button
                            onClick={exportPdf}
                            disabled={!data || pdfBusy}
                            className="btn btn-sm d-flex align-items-center gap-1 px-3"
                            style={{ borderRadius: 999, background: ORANGE, color: "#fff", fontWeight: 600, border: "none" }}
                        >
                            {pdfBusy ? (
                                <><Spinner size="sm" style={{ width: 14, height: 14 }} /> Hazırlanıyor…</>
                            ) : (
                                <><i className="bx bxs-file-pdf"></i> PDF İndir</>
                            )}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center p-5">
                        <Spinner style={{ color: NAVY }} />
                        <div className="mt-2 text-muted">Analiz yükleniyor...</div>
                    </div>
                ) : error ? (
                    <Alert color="danger" className="border-0 shadow-sm">{error}</Alert>
                ) : (
                    <>
                        {/* GENEL İSTATİSTİKLER */}
                        <SectionTitle>Genel İstatistikler</SectionTitle>
                        <Row className="g-3 mb-2 kpi-row">
                            <Col xs={6} md={4} xl={2}>
                                <KpiCard accent icon="bx-layer" label="Toplam Tahsilat"
                                    value={fmtMoney2(totals.grossAmount)} change={kpis?.totalAmountChange} />
                            </Col>
                            <Col xs={6} md={4} xl={2}>
                                <KpiCard icon="bx-receipt" label="İşlem Sayısı"
                                    value={trNum(totals.transactionCount)}
                                    change={kpis?.totalTransactionsChange} />
                            </Col>
                            <Col xs={6} md={4} xl={2}>
                                {/* Taksit dağılımı yalnız BAŞARILI işlemler üzerinden hesaplanır;
                                    başarısız bir denemenin taksit tercihi tahsilatı temsil etmez. */}
                                <KpiCard icon="bx-credit-card-front" label="Taksit Dağılımı"
                                    value={kpis ? `%${(kpis.installmentRate ?? 0).toFixed(0)}` : "—"}
                                    />
                            </Col>
                            <Col xs={6} md={4} xl={2}>
                                {/* Kesilen toplam komisyon: firmanın gideri, platformun ise
                                    içinden yalnız hakediş payı kalan havuzu. */}
                                <KpiCard icon="bx-receipt" label={isSuperAdmin ? "Toplam Komisyon" : "Komisyon Kesintisi"}
                                    value={fmtMoney2(totals.commissionAmount)}
                                    sub={isSuperAdmin && providerShare != null
                                        ? `${fmtMoney2(providerShare)} sağlayıcılara`
                                        : null} />
                            </Col>
                            <Col xs={6} md={4} xl={2}>
                                <KpiCard icon="bx-wallet" label="Net Tutar"
                                    value={fmtMoney2(totals.netAmount)}
                                    sub={Number(totals.refundedAmount) > 0
                                        ? `${fmtMoney2(totals.refundedAmount)} iade düşüldü`
                                        : null} />
                            </Col>
                            <Col xs={6} md={4} xl={2}>
                                <KpiCard icon="bx-calendar-check" label="Valörü Bu Dönemde"
                                    value={fmtMoney2(totals.valorAmount)}
                                    sub={pendingValor > 0
                                        ? `${fmtMoney2(pendingValor)} sonraki dönemde yatacak`
                                        : null} />
                            </Col>
                            <Col xs={6} md={4} xl={2}>
                                {/* Süper admin platformun payını, firma kendi efektif oranını görür. */}
                                {isSuperAdmin ? (
                                    <KpiCard icon="bx-trending-up" label="Komisyon Kazancı"
                                        value={totals.commissionEarning != null ? fmtMoney2(totals.commissionEarning) : "—"}
                                        sub={earningShareOfCommission != null
                                            ? `toplam komisyonun %${earningShareOfCommission.toFixed(1)}'i`
                                            : "hakediş oranı girilmemiş"} />
                                ) : (
                                    <KpiCard icon="bx-percentage" label="Ort. Komisyon Oranı"
                                        value={effectiveRate != null ? `%${effectiveRate.toFixed(2)}` : "—"}
                                        sub="kesilen komisyon / matrah" />
                                )}
                            </Col>
                        </Row>

                        {/* GRAFİK ANALİZİ */}
                        <SectionTitle>Grafik Analizi</SectionTitle>
                        <Row className="g-3 mb-2">
                            {/* Trend (bar + line) */}
                            <Col lg={7}>
                                <div className="bg-white p-4 h-100" style={{ borderRadius: 16, border: "1px solid #eef0f4" }}>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <h5 className="fw-bold mb-1" style={{ color: NAVY }}>Tahsilat &amp; İşlem Trendi</h5>
                                            <div className="text-muted" style={{ fontSize: ".8rem" }}>{trendGranularity} — {showCompanyScope ? me.companyName : "tüm POS entegrasyonları"}</div>
                                        </div>
                                        <span
                                            className="d-inline-flex align-items-center"
                                            style={{ background: "#eef2f9", color: NAVY, fontWeight: 600, fontSize: ".76rem", padding: ".3rem .7rem", borderRadius: 999, whiteSpace: "nowrap" }}
                                        >
                                            <i className="bx bx-calendar me-1" style={{ verticalAlign: "middle" }}></i>
                                            {trendRange}
                                        </span>
                                    </div>
                                    <div className="d-flex gap-4 mb-2" style={{ fontSize: ".78rem" }}>
                                        <span><span style={{ display: "inline-block", width: 10, height: 10, background: NAVY, borderRadius: 3, marginRight: 6 }} />Tahsilat</span>
                                        <span><span style={{ display: "inline-block", width: 10, height: 10, background: ORANGE, borderRadius: 3, marginRight: 6 }} />İşlem Adedi</span>
                                    </div>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <ComposedChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
                                            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#8a93a6" }} axisLine={false} tickLine={false} />
                                            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#8a93a6" }} axisLine={false} tickLine={false}
                                                tickFormatter={(v) => fmtCompactTL(v)} />
                                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#8a93a6" }} axisLine={false} tickLine={false}
                                                tickFormatter={(v) => trNum(v)} />
                                            <Tooltip
                                                formatter={(value, name) =>
                                                    name === "amount" ? [fmtTL(value), "Tahsilat"] : [trNum(value), "İşlem"]
                                                }
                                                labelStyle={{ color: NAVY, fontWeight: 600 }}
                                            />
                                            <Bar yAxisId="left" dataKey="amount" fill={NAVY} radius={[6, 6, 0, 0]} maxBarSize={48} opacity={0.85} />
                                            <Line yAxisId="right" type="monotone" dataKey="transactionCount" stroke={ORANGE} strokeWidth={2.5}
                                                dot={{ r: 4, fill: ORANGE }} />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </Col>

                            {/* Donut dağılımı */}
                            <Col lg={5}>
                                <div className="bg-white p-4 h-100" style={{ borderRadius: 16, border: "1px solid #eef0f4" }}>
                                    <h5 className="fw-bold mb-1" style={{ color: NAVY }}>POS Dağılımı</h5>
                                    <div className="text-muted mb-3" style={{ fontSize: ".8rem" }}>Sağlayıcı bazında tahsilat oranı</div>
                                    {distribution.length === 0 ? (
                                        <div className="text-center text-muted py-5">Bu dönemde tahsilat verisi yok.</div>
                                    ) : (
                                        <>
                                            <div style={{ position: "relative" }}>
                                                <ResponsiveContainer width="100%" height={220}>
                                                    <PieChart>
                                                        <Pie data={distribution} dataKey="amount" nameKey="providerLabel"
                                                            cx="50%" cy="50%" innerRadius={62} outerRadius={88} paddingAngle={2}>
                                                            {distribution.map((entry, i) => (
                                                                <Cell key={i} fill={SLICE_COLORS[i % SLICE_COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip formatter={(value) => fmtTL(value)} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center" }}>
                                                    <div className="fw-bold" style={{ color: NAVY, fontSize: ".95rem" }}>{fmtMoney2(totalDistAmount)}</div>
                                                    <div className="text-muted" style={{ fontSize: ".72rem" }}>Toplam</div>
                                                </div>
                                            </div>
                                            <div className="mt-3">
                                                {distribution.map((d, i) => (
                                                    <div key={i} className="d-flex align-items-center justify-content-between py-1" style={{ fontSize: ".82rem" }}>
                                                        <span className="d-flex align-items-center gap-2">
                                                            <span style={{ width: 10, height: 10, borderRadius: 3, background: SLICE_COLORS[i % SLICE_COLORS.length] }} />
                                                            {d.providerLabel}
                                                        </span>
                                                        <span className="d-flex align-items-center gap-2">
                                                            <span className="fw-semibold text-dark">{fmtMoney2(d.amount)}</span>
                                                            <span className="text-muted">{(d.percentage ?? 0).toFixed(0)}%</span>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        {/* POS ENTEGRASYON DETAYLARI — hiyerarşinin giriş noktası */}
                        <SectionTitle>POS Entegrasyon Detayları</SectionTitle>
                        <div className="text-muted mb-2" style={{ fontSize: ".78rem" }}>
                            Bir POS sağlayıcısına tıklayarak firma kırılımına inebilirsiniz.
                        </div>
                        <PosAdvancedFilters
                            fields={FILTER_FIELDS}
                            values={draft}
                            onChange={set}
                            onCommit={commit}
                            onReset={reset}
                            activeCount={activeCount}
                        />
                        <PosScopeTable
                            rows={details}
                            totals={data.detailsTotals}
                            showAdminColumns={isSuperAdmin}
                            emptyText={activeCount
                                ? "Filtrelere uyan POS sağlayıcısı bulunmuyor."
                                : "Bu dönemde POS işlemi bulunmuyor."}
                            identityColumns={[
                                {
                                    key: "provider",
                                    label: "POS Sağlayıcı",
                                    className: "fw-semibold text-dark",
                                    sortKey: "provider",
                                    width: null, // iki satırlı hücre; kısaltma uygulanmaz
                                    render: (d) => (
                                        // Ad üstte, durum bilgisi altında (uzun adlarda yan yana
                                        // sığmıyordu). Süper adminde "aktif/pasif" bilgi vermez —
                                        // her sağlayıcı er geç bir firmada kuruludur — bunun yerine
                                        // kaç firmada kurulu olduğu yazılır.
                                        <div className="d-flex flex-column align-items-start gap-1">
                                            <span>{d.providerLabel}</span>
                                            {isSuperAdmin ? (
                                                <span
                                                    className="d-inline-flex align-items-center"
                                                    style={{
                                                        background: d.configuredCompanyCount > 0 ? "#eef2f9" : "#fdecea",
                                                        color: d.configuredCompanyCount > 0 ? "#5c6a86" : "#c0392b",
                                                        fontWeight: 600,
                                                        fontSize: ".7rem",
                                                        lineHeight: 1,
                                                        padding: ".26rem .5rem",
                                                        borderRadius: 999,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                    title={d.configuredCompanyCount > 0
                                                        ? "Bu sağlayıcının ayarları kaç firmada tanımlı"
                                                        : "İşlem var ama hiçbir firmada ayar tanımlı değil"}
                                                >
                                                    {d.configuredCompanyCount > 0
                                                        ? `${trNum(d.configuredCompanyCount)} firmada kurulu`
                                                        : "Kurulu değil"}
                                                </span>
                                            ) : (
                                                <span
                                                    className="d-inline-flex align-items-center"
                                                    style={{
                                                        background: d.active ? "#e7f6ee" : "#f0f1f4",
                                                        color: d.active ? "#1E8F5B" : "#8a93a6",
                                                        fontWeight: 600,
                                                        fontSize: ".7rem",
                                                        lineHeight: 1,
                                                        padding: ".26rem .5rem",
                                                        borderRadius: 999,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    <i className="bx bxs-circle me-1" style={{ fontSize: ".45rem", verticalAlign: "middle" }}></i>
                                                    {d.active ? "Aktif" : "Pasif"}
                                                </span>
                                            )}
                                        </div>
                                    ),
                                },
                            ]}
                            sort={sort}
                            direction={direction}
                            onSort={toggle}
                            isRowClickable={(d) => !!d.provider && d.provider !== "UNKNOWN"}
                            onRowClick={(d) => navigate(`/admin/pos-raporu/${d.provider}`)}
                        />
                        <div style={{ height: 24 }} />
                    </>
                )}
            </Container>
        </div>
    );
}
